import { FC, useRef, useState } from 'react'
import { Box, Button, Paper, Stack, Typography } from '@mui/material'
import { Location } from '../../../../api/models'
import { getPermitManagedByDisplayString, hasStatusAction } from '../../../../utils/permitUtils'
import { PermitColumn } from './PermitColumn'
import { GridColumn } from './GridColumn'
import { AgencyTypeValues } from '../../../../constants/AgencyType'
import LoadingButton from '@mui/lab/LoadingButton'
import { ActionFunctions } from './PermitColumnEditor'

type PermitsTableProps = {
  location: Location
}

/**
 * PermitsTable component.
 * Renders a permits table filled with permit-related data.
 */
export const PermitsTable: FC<PermitsTableProps> = ({ location }) => {
  const [editingAgencyType, setEditingAgencyType] = useState<AgencyTypeValues | null>(null)

  const actionFnsRef = useRef<ActionFunctions>(undefined)

  const captureEmittedFunctions = (actionFns: ActionFunctions) => {
    actionFnsRef.current = actionFns
  }

  const handleCancel = () => {
    setEditingAgencyType(null)
    if (actionFnsRef.current) {
      actionFnsRef.current.cancel()
    }
    actionFnsRef.current = undefined
  }

  const handleSave = () => {
    if (actionFnsRef.current) {
      actionFnsRef.current.save()
    }
  }

  const actionButtons = (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
      <Button variant="outlined" data-testid={'edit-permit-cancel-button'} onClick={handleCancel}>
        Cancel
      </Button>
      <LoadingButton variant="contained" data-testid={'edit-permit-save-button'} onClick={handleSave}>
        Save
      </LoadingButton>
    </Box>
  )

  return (
    <Paper data-testid="permits">
      <Stack direction={'row'} justifyContent={'space-between'}>
        <Typography variant={'h2'} p={'var(--spacing-s)'}>
          Permits
        </Typography>
        <Typography p={'var(--spacing-s)'} data-testid={'permit-managed-by'}>
          {getPermitManagedByDisplayString(location)}
        </Typography>
      </Stack>
      <Box display={'grid'}>
        <GridColumn col={1}>
          <Box></Box>
          {(hasStatusAction(location, 'fire') || hasStatusAction(location, 'police')) && <Box data-noborder></Box>}
          <Box sx={{ padding: 'var(--spacing-s)', fontWeight: 500 }}>NUMBER</Box>
          <Box sx={{ padding: 'var(--spacing-s)', fontWeight: 500 }}>EXPIRATION</Box>
          <Box sx={{ padding: 'var(--spacing-s)', fontWeight: 500 }}>RENEWAL CADENCE</Box>
          <Box sx={{ padding: 'var(--spacing-s)', fontWeight: 500 }}>REQUIREMENT</Box>
          <Box sx={{ padding: 'var(--spacing-s)', fontWeight: 500 }}>APPLICATION</Box>
          <Box sx={{ padding: 'var(--spacing-s)', fontWeight: 500 }}>ORDINANCE</Box>
          <Box sx={{ padding: 'var(--spacing-s)', fontWeight: 500 }}>VERIFIED RESPONSE REQUIREMENT</Box>
          <Box sx={{ padding: 'var(--spacing-s)', fontWeight: 500 }}>MULTIPLE ALARM TRIGGERS REQUIREMENT</Box>
          <Box sx={{ padding: 'var(--spacing-s)', fontWeight: 500 }}>CALL VERIFICATION REQUIREMENT</Box>
          <Box sx={{ padding: 'var(--spacing-s)', fontWeight: 500 }}>GUARD SERVICE</Box>
          <Box sx={{ padding: 'var(--spacing-s)', fontWeight: 500 }}>ADDITIONAL NOTES</Box>
        </GridColumn>
        <PermitColumn
          agencyType={'police'}
          location={location}
          col={2}
          editingAgencyType={editingAgencyType}
          onEditClicked={() => {
            setEditingAgencyType('police')
          }}
          emitFunctions={captureEmittedFunctions}
          onCloseEditors={() => {
            setEditingAgencyType(null)
          }}
        />
        <PermitColumn
          agencyType={'fire'}
          location={location}
          col={3}
          editingAgencyType={editingAgencyType}
          onEditClicked={() => {
            setEditingAgencyType('fire')
          }}
          emitFunctions={captureEmittedFunctions}
          actionButtons={actionButtons}
          onCloseEditors={() => {
            setEditingAgencyType(null)
          }}
        />
      </Box>
    </Paper>
  )
}
